// Duplicate-detection scans shared by the Transactions and Recurring tabs.
//
// Match criteria (per the spec):
//   - Same property (null/shared counts as a property too)
//   - Same month (transactions) OR same frequency (recurring schedules)
//   - Amounts within 5% of each other
// Confidence:
//   - Category match → HIGH confidence (probably duplicate)
//   - Category mismatch → LOW confidence (possible duplicate, maybe miscategorized)
//
// Pairs only — if three items in a group cluster together we surface (A,B),
// (A,C), (B,C) separately. Lets the user decide which to keep without us
// guessing the "canonical" one.

const DEFAULT_TOLERANCE = 0.05; // 5%

// Same-sign is naturally enforced — comparing +1500 and -1500 yields a 200%
// gap, well above the 5% threshold. So an income and an expense of equal
// magnitude won't be flagged as duplicates of each other.
function amountsClose(a, b, tolerance = DEFAULT_TOLERANCE) {
  const numA = Number(a), numB = Number(b);
  if (!isFinite(numA) || !isFinite(numB)) return false;
  const diff = Math.abs(numA - numB);
  const ref = Math.max(Math.abs(numA), Math.abs(numB));
  if (ref === 0) return numA === numB;
  return diff / ref <= tolerance;
}

function categoriesMatch(a, b) {
  return (a || '') === (b || '');
}

// Group items by a key, then enumerate (i,j) pairs with i<j inside each
// group that pass the matcher. The matcher returns the confidence label
// ('high' | 'low') or null to skip.
function pairsByGroup(items, keyOf, matcher) {
  const groups = new Map();
  for (const item of items) {
    const key = keyOf(item);
    if (key === null) continue;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(item);
  }
  const pairs = [];
  for (const group of groups.values()) {
    for (let i = 0; i < group.length; i++) {
      for (let j = i + 1; j < group.length; j++) {
        const confidence = matcher(group[i], group[j]);
        if (confidence) pairs.push({ a: group[i], b: group[j], confidence });
      }
    }
  }
  // High-confidence first; within a tier, larger amounts first (more impact)
  pairs.sort((x, y) => {
    if (x.confidence !== y.confidence) return x.confidence === 'high' ? -1 : 1;
    return Math.abs(y.a.amount) - Math.abs(x.a.amount);
  });
  return pairs;
}

// Bucket by property + YYYY-MM. A null property_id becomes the synthetic
// "__shared__" bucket so all-properties-shared transactions can still pair
// with each other.
export function findTransactionDuplicates(transactions) {
  return pairsByGroup(
    transactions,
    t => {
      if (!t.transaction_date) return null;
      return `${t.property_id || '__shared__'}|${t.transaction_date.slice(0, 7)}`;
    },
    (a, b) => {
      if (!amountsClose(a.amount, b.amount)) return null;
      return categoriesMatch(a.category, b.category) ? 'high' : 'low';
    }
  );
}

// Recurring has no transaction_date in the same sense — the "same month"
// criterion translates to "same frequency" (two monthly schedules competing
// for the same monthly slot are the natural recurring duplicate). Day of
// month not considered — close-enough days still represent the same logical
// schedule.
export function findRecurringDuplicates(recurring) {
  return pairsByGroup(
    recurring,
    r => `${r.property_id || '__shared__'}|${r.frequency || '__unknown__'}`,
    (a, b) => {
      if (!amountsClose(a.amount, b.amount)) return null;
      return categoriesMatch(a.category, b.category) ? 'high' : 'low';
    }
  );
}
